import {
  ThemeBreakpointsSchema,
  ThemeButtonVariantsSchema,
  ThemeColorKey,
  ThemeColorsSchema,
  ThemeFontFamilyItemSchema,
  ThemeFontFamilySchema,
  ThemeProps,
  ThemeShadowSchema,
  ThemeTextVariantsSchema,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import { FONT_WEIGHT_MAP } from '@mutiny-pkg/tailwind-parser';
import { LexicalThemeColors } from '../../../richtext/types';
import { parameterize } from '@mutiny-pkg/formatters';
import z from 'zod';

export const TAILWIND_CSS_CLASS = 'mutiny-tw';
type FlexiblePageThemeVariableName =
  | `--color-${string}`
  | `--font-${string}`
  | `--shadow-${'sm' | 'md' | 'lg' | 'xl' | '2xl'}`
  | `--breakpoint-${'sm' | 'md' | 'lg'}`;

export type FontFaceType = {
  fontFamily: string;
  fontWeight: string;
  src: string;
  fontStyle: string;
};

export const getFontFamilies = (themeProps: ThemeProps) => {
  const fonts: z.infer<typeof ThemeFontFamilyItemSchema>[] = [];
  const fontFamilyTheme = ThemeFontFamilySchema.strip().parse(themeProps);
  fontFamilyTheme.fonts.forEach((font) => {
    if (font.family) {
      fonts.push(font);
    }
  });
  return fonts;
};

// Font loading function similar to useFonts.ts
export const loadFontFace = async (fontFamily: string, fontWeight: string, src: string, style?: string) => {
  if (!fontFamily || !fontWeight || !src) {
    return;
  }

  const fontFace = new FontFace(fontFamily, `url(${src})`, { weight: fontWeight, style: style || 'normal' });
  document.fonts.add(fontFace);
  try {
    await fontFace.load();
  } catch (error) {
    console.warn('Error loading font face', error);
  }
};

// Process theme font families into loadable font faces
export const processFontFamily = (fontFamilies?: z.infer<typeof ThemeFontFamilyItemSchema>[]): FontFaceType[] => {
  if (!fontFamilies || !Array.isArray(fontFamilies)) return [];

  const fontFaces: FontFaceType[] = [];

  fontFamilies.forEach((family) => {
    family.variants?.forEach((variant) => {
      if (variant.url) {
        fontFaces.push({
          fontFamily: family.family,
          fontWeight: FONT_WEIGHT_MAP[variant.weight] || variant.weight,
          src: variant.url,
          fontStyle: variant.style,
        });
      }
    });
  });

  return fontFaces;
};

// Generate Tailwind CSS variable overrides from flattened theme keys
export const generateTailwindVariables = (
  theme: ThemeProps,
): Partial<Record<FlexiblePageThemeVariableName, string>> => {
  const variables: Partial<Record<FlexiblePageThemeVariableName, string>> = {};

  // Font families
  const fontFamilyTheme = ThemeFontFamilySchema.strip().parse(theme);
  (Object.keys(fontFamilyTheme) as Array<keyof typeof fontFamilyTheme>).forEach((key) => {
    fontFamilyTheme[key].forEach((font) => {
      if (font.family) {
        variables[`--font-${font.name}` as FlexiblePageThemeVariableName] = font.family;
      }
    });
  });

  // Colors - always emit core color tokens from schema defaults, plus any additional colors.
  const baseColorsTheme = ThemeColorsSchema.strip().parse(theme);
  variables['--color-primary'] = baseColorsTheme['colors.primary'].color;
  variables['--color-secondary'] = baseColorsTheme['colors.secondary'].color;
  variables['--color-tertiary'] = baseColorsTheme['colors.tertiary'].color;
  variables['--color-dark'] = baseColorsTheme['colors.dark'].color;
  variables['--color-light'] = baseColorsTheme['colors.light'].color;
  baseColorsTheme['colors.additional'].forEach(({ name, color }) => {
    const identifier = parameterize(name);
    variables[`--color-${identifier}` as FlexiblePageThemeVariableName] = color;
  });

  // Shadows
  const shadowTheme = ThemeShadowSchema.strip().parse(theme);
  (Object.keys(shadowTheme) as Array<keyof typeof shadowTheme>).forEach((key) => {
    const value = shadowTheme[key];
    const size = key.replace('shadow.', '');
    if (value) {
      variables[`--shadow-${size}` as FlexiblePageThemeVariableName] = value;
    }
  });

  // Breakpoints
  const breakpointsTheme = ThemeBreakpointsSchema.strip().parse(theme);
  (Object.keys(breakpointsTheme) as Array<keyof typeof breakpointsTheme>).forEach((key) => {
    const value = breakpointsTheme[key];
    const size = key.replace('breakpoints.', '');
    if (value) {
      variables[`--breakpoint-${size}` as FlexiblePageThemeVariableName] = value;
    }
  });

  return variables;
};

export const excludeBreakpointVariables = (
  themeVariables: Partial<Record<FlexiblePageThemeVariableName, string>>,
): Partial<Record<Exclude<FlexiblePageThemeVariableName, `--breakpoint-${string}`>, string>> => {
  return Object.fromEntries(
    Object.entries(themeVariables).filter(([variableName]) => !variableName.startsWith('--breakpoint-')),
  ) as Partial<Record<Exclude<FlexiblePageThemeVariableName, `--breakpoint-${string}`>, string>>;
};

export const generateInlineThemeVariables = (
  themeVariables: Partial<Record<FlexiblePageThemeVariableName, string>>,
): string[] => {
  const scopedThemeVariables = excludeBreakpointVariables(themeVariables);
  const scopedVariableEntries = Object.keys(scopedThemeVariables).map((key) => `${key}: var(${key});`);
  const breakpointEntries = Object.entries(themeVariables).flatMap(([key, value]) => {
    if (!key.startsWith('--breakpoint-') || !value) {
      return [];
    }
    return `${key}: ${value};`;
  });

  return [...scopedVariableEntries, ...breakpointEntries];
};

// Generate @layer components CSS for all variant definitions
export const generateVariantComponents = (theme: ThemeProps): string => {
  const components: string[] = [`.${TAILWIND_CSS_CLASS} { @apply text-dark; }`];

  // Process button variants
  const buttonVariantsTheme = ThemeButtonVariantsSchema.strip().parse(theme);
  (Object.keys(buttonVariantsTheme) as Array<keyof typeof buttonVariantsTheme>).forEach((key) => {
    const variantName = key.replace('variants.button.', '');
    const variant = buttonVariantsTheme[key];

    if (variant) {
      // Generate base class
      if (variant.base) {
        const utilities = Object.values(variant.base).filter((value) => value !== undefined);
        if (utilities.length) {
          components.push(`.${TAILWIND_CSS_CLASS} .btn-${variantName} { @apply ${utilities.join(' ')} }`);
        }
      }

      // Generate hover class
      if (variant.hover) {
        const utilities = Object.values(variant.hover).filter((value) => value !== undefined);
        if (utilities.length) {
          components.push(`.${TAILWIND_CSS_CLASS} .btn-${variantName}:hover { @apply ${utilities.join(' ')} }`);
        }
      }
    }
  });

  // Process text variants
  const textVariantsTheme = ThemeTextVariantsSchema.strip().parse(theme);
  (Object.keys(textVariantsTheme) as Array<keyof typeof textVariantsTheme>).forEach((key) => {
    const variantName = key.replace('variants.text.', '');
    const variant = textVariantsTheme[key];

    if (variant) {
      // Generate base class with all non-hover properties
      const utilities = Object.values(variant).filter((value) => value !== undefined);
      if (utilities.length) {
        components.push(`.${TAILWIND_CSS_CLASS} .text-${variantName} { @apply ${utilities.join(' ')} }`);
      }
    }
  });

  return `@layer components {\n    ${components.join('\n    ')}\n  }`;
};

export type ThemeColor = {
  label: string;
  value: string;
  colorValue: string;
  tokenName?: string;
};

export const getColorsFromTheme = (theme: ThemeProps): ThemeColor[] => {
  const colorsTheme = ThemeColorsSchema.strip().parse(theme);
  const colors: ThemeColor[] = [];

  // Keys that should be excluded from the output if not present in the original theme
  const optionalColorKeys = new Set([ThemeColorKey.Secondary, ThemeColorKey.Tertiary]);

  (Object.keys(colorsTheme) as Array<keyof typeof colorsTheme>).forEach((key) => {
    if (key === 'colors.additional') {
      colorsTheme['colors.additional'].forEach(({ name, color }) => {
        const identifier = parameterize(name);
        colors.push({
          label: name,
          value: identifier,
          colorValue: color,
        });
      });
    } else {
      // Skip optional colors that don't exist in the original theme
      if (optionalColorKeys.has(key as ThemeColorKey) && theme[key] === undefined) {
        return;
      }
      const colorKey = key.replace('colors.', '');
      colors.push({
        label: colorsTheme[key].name,
        value: colorKey,
        colorValue: colorsTheme[key].color,
        tokenName: colorKey,
      });
    }
  });
  return colors;
};

export const getLexicalThemeColors = (theme: ThemeProps): LexicalThemeColors => {
  const colors = getColorsFromTheme(theme);

  // Support both old schema (textDark/textLight) and new schema (dark/light)
  // Try new schema first, fall back to old schema for backwards compatibility
  const textDarkColor =
    colors.find((c) => c.tokenName === 'dark')?.colorValue ??
    colors.find((c) => c.tokenName === 'textDark')?.colorValue ??
    '';

  const textLightColor =
    colors.find((c) => c.tokenName === 'light')?.colorValue ??
    colors.find((c) => c.tokenName === 'textLight')?.colorValue ??
    '';

  return {
    color: {
      primary: colors.find((c) => c.tokenName === 'primary')?.colorValue ?? '',
      secondary: colors.find((c) => c.tokenName === 'secondary')?.colorValue ?? '',
      tertiary: colors.find((c) => c.tokenName === 'tertiary')?.colorValue ?? '',
      default: colors.find((c) => c.tokenName === 'default')?.colorValue ?? '',
      textDark: textDarkColor,
      textLight: textLightColor,
      none: '',
      unset: '',
    },
    additionalColors: colors.filter((c) => c.tokenName === undefined).map((c) => c.colorValue),
  };
};
